import { EventEmitter } from "utils/helpers/classes/event-emitter";
import { CommandAction } from "./command-action";

type CommandControllerEmitter = {
  history: (controller: CommandController) => void;
};

type CommandControllerOptions = {
  limit?: number;
};

export class CommandController {
  private readonly _limit: number;

  private readonly _emitter = new EventEmitter<CommandControllerEmitter>();

  public get events() {
    return this._emitter.events;
  }

  private _currentUndoIndex = -1;

  public get currentHistory() {
    return this._currentUndoIndex;
  }

  private set currentHistory(value: number) {
    this._currentUndoIndex = value;
  }

  private _undoableActions: CommandAction[] = [];

  public get historyLength() {
    return this._undoableActions.length;
  }

  public get canUndo(): boolean {
    return !this._emitter.isBlocked && !!this.historyLength && this.currentHistory > -1;
  }

  public get canRedo(): boolean {
    return (
      !this._emitter.isBlocked &&
      !!this.historyLength &&
      this.currentHistory + 1 !== this.historyLength
    );
  }

  constructor(options: CommandControllerOptions = {}) {
    const { limit = 50 } = options;

    this._limit = Math.max(Math.floor(limit), 0);
  }

  public redo(delta = 1) {
    if (this._emitter.isBlocked) {
      console.warn(
        "You are trying to 'redo', but Emitter is blocked via '.events.block()'. " +
          "Please ensure that you '.events.unblock()' emitter, so you can emit changes",
      );
      return;
    }

    if (this.currentHistory + 1 === this.historyLength) return;

    delta = Math.floor(delta);

    if (delta < 1 || !Number.isFinite(delta)) {
      throw new Error("Cannot do 'redo' command with delta '< 1' or 'non finite number'");
    }

    for (let i = 0; i < delta; i++) {
      if (this.currentHistory + 1 === this.historyLength) break;

      const currentAction = this._undoableActions[this.currentHistory + 1];
      currentAction.execute();

      this.currentHistory += 1;
    }

    this._emitHistory();
  }

  public undo(delta = 1) {
    if (this._emitter.isBlocked) {
      console.warn(
        "You are trying to 'undo', but Emitter is blocked via '.events.block()'. " +
          "Please ensure that you '.events.unblock()' emitter, so you can emit changes",
      );
      return;
    }

    if (this.currentHistory === -1) return;

    delta = Math.floor(delta);

    if (delta < 1 || !Number.isFinite(delta)) {
      throw new Error("Cannot do 'undo' command with delta '< 1' or 'non finite number'");
    }

    for (let i = 0; i < delta; i++) {
      if (this.currentHistory === -1) break;

      const currentAction = this._undoableActions[this.currentHistory];

      if (!currentAction.undoAble) {
        throw new Error("Action is not undoAble. It's more likely code syntax error");
      }
      currentAction.undo();

      this.currentHistory -= 1;
    }

    this._emitHistory();
  }

  public clear() {
    this._currentUndoIndex = -1;
    this._undoableActions = [];
  }

  public execute(
    name: string,
    action: VoidFunction,
    undo?: VoidFunction,
    clear?: VoidFunction,
  ): void;
  public execute(action: CommandAction): void;
  public execute(a: any, b?: any, c?: any, d?: any) {
    // if (this._emitter.isBlocked) {
    //   console.warn(
    //     "You are trying to 'execute', but Emitter is blocked via '.events.block()'. " +
    //       "Please ensure that you '.events.unblock()' emitter, so you can emit changes",
    //   );
    //   return this;
    // }

    const commandAction = a instanceof CommandAction ? a : new CommandAction(a, b, c, d);

    if (commandAction.undoAble) {
      // If there are actions ahead current history, then clear them.
      if (this.currentHistory + 1 !== this.historyLength) {
        const cutActions = this._undoableActions.splice(
          this.currentHistory + 1,
          this.historyLength,
        );

        for (let i = 0; i < cutActions.length; i++) {
          cutActions[i].clear();
        }
      }

      // Push current action as latest one.
      this._undoableActions.push(commandAction);

      // If we push action and reach the actions limit,
      // remove first action in queue
      if (this.historyLength > this._limit) {
        this._undoableActions.shift()?.clear();
      }

      this.currentHistory = this.historyLength - 1;
      this._emitHistory();
    }

    commandAction.execute();

    return this;
  }

  private _emitHistory() {
    this._emitter.emit("history", this);
  }
}
